import {
	clamp,
	interpolateMat3,
	invertMat3,
	isEmptyMat3,
	multiplyMat3,
	multiplyMat3ByVec3,
	vec3ToMat3Diagonal,
} from "@/core/utils/math"
import { mat3, vec2, vec3 } from "gl-matrix"
import { XYCoord } from "./xy-coord"
import { DNGTemperature } from "./dng-temperature"
import { DNGCalibrationIlluminant } from "./dng-calibration-illuminant"
import { GenericWhiteBalance } from "./generic-white-balance"
import { RawInfo } from "@/lib/raw/fastlib"

export class DNGColorWhiteBalance extends GenericWhiteBalance {
	/* Input values */

	private channels = 3

	private temperature1 = 0
	private temperature2 = 0

	private colorMatrix1: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]
	private colorMatrix2: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

	private forwardMatrix1: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]
	private forwardMatrix2: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

	private reductionMatrix1: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]
	private reductionMatrix2: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

	private cameraCalibration1: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]
	private cameraCalibration2: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

	private analogBalance: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

	/* Computed data */

	private computedWhite = new XYCoord()
	private computedCamToPCS: mat3 = mat3.create()
	private computedPCSToCam: mat3 = mat3.create()
	private computedCameraWhite: vec3 = [1, 1, 1]

	public get neutralToXY(): vec2 {
		return [this.computedWhite.x, this.computedWhite.y]
	}

	public get camToPCS(): mat3 {
		return this.computedCamToPCS
	}

	public get PCSToCam(): mat3 {
		return this.computedPCSToCam
	}

	public get whiteBalanceMultipliers(): vec3 {
		return this.computedCameraWhite
	}

	public get whiteBalance(): { tint: number; temperature: number } {
		const tmp = new DNGTemperature(this.computedWhite)

		return {
			tint: tmp.tint,
			temperature: tmp.temperature,
		}
	}

	constructor(info: RawInfo) {
		super(info)
		this.compute()
	}

	private compute() {
		/* Temperature */

		this.temperature1 = this.convertIlluminantToTemperature(
			this.info.calibration_illuminant1 ?? 1,
		)

		this.temperature2 = this.convertIlluminantToTemperature(
			this.info.calibration_illuminant2 ?? 1,
		)

		/* Color matrix */

		if (this.info.color_mat1) {
			this.colorMatrix1 = Array.from(this.info.color_mat1) as mat3
		} else {
			this.colorMatrix1 = mat3.create()
		}

		if (this.info.color_mat2) {
			this.colorMatrix2 = Array.from(this.info.color_mat2) as mat3
		}

		/* Forward matrix (not parsed) */

		// Nothing here yet

		/* Reduction matrix */

		if (this.info.reduction_mat1) {
			this.reductionMatrix1 = Array.from(this.info.reduction_mat1) as mat3
		}

		if (this.info.reduction_mat2) {
			this.reductionMatrix2 = Array.from(this.info.reduction_mat2) as mat3
		}

		/* Camera calibration */

		if (this.info.camera_calibration1 && this.info.camera_calibration1.length >= 9) {
			this.cameraCalibration1 = Array.from(this.info.camera_calibration1) as mat3
		} else {
			this.cameraCalibration1 = mat3.create()
		}

		if (this.info.camera_calibration2 && this.info.camera_calibration2.length >= 9) {
			this.cameraCalibration2 = Array.from(this.info.camera_calibration2) as mat3
		} else {
			this.cameraCalibration2 = mat3.create()
		}

		/* Analog Balance */

		if (this.info.analog_balance && this.info.analog_balance.length >= 3) {
			this.analogBalance = vec3ToMat3Diagonal(Array.from(this.info.analog_balance) as vec3)
		} else {
			this.analogBalance = mat3.create()
		}

		/* Normalization and order fixes */

		this.forwardMatrix1 = this.normalizeForwardMatrix(this.forwardMatrix1)

		this.colorMatrix1 = multiplyMat3(
			multiplyMat3(this.analogBalance, this.cameraCalibration1),
			this.colorMatrix1,
		)

		if (
			isEmptyMat3(this.colorMatrix2) ||
			this.temperature1 <= 0 ||
			this.temperature2 <= 0 ||
			this.temperature1 == this.temperature2
		) {
			this.temperature1 = 5000
			this.temperature2 = 5000

			this.colorMatrix2 = this.colorMatrix1
			this.forwardMatrix2 = this.forwardMatrix1
			this.reductionMatrix2 = this.reductionMatrix1
			this.cameraCalibration2 = this.cameraCalibration1
		} else {
			this.forwardMatrix2 = this.normalizeForwardMatrix(this.forwardMatrix2)

			this.colorMatrix2 = multiplyMat3(
				multiplyMat3(this.analogBalance, this.cameraCalibration2),
				this.colorMatrix2,
			)

			if (this.temperature1 > this.temperature2) {
				{
					const temp = this.temperature1
					this.temperature1 = this.temperature2
					this.temperature2 = temp
				}

				{
					const temp = this.colorMatrix1
					this.colorMatrix1 = this.colorMatrix2
					this.colorMatrix2 = temp
				}

				{
					const temp = this.forwardMatrix1
					this.forwardMatrix1 = this.forwardMatrix2
					this.forwardMatrix2 = temp
				}

				{
					const temp = this.reductionMatrix1
					this.reductionMatrix1 = this.reductionMatrix2
					this.reductionMatrix2 = temp
				}

				{
					const temp = this.cameraCalibration1
					this.cameraCalibration1 = this.cameraCalibration2
					this.cameraCalibration2 = temp
				}
			}
		}

		/* White balance */

		const neutral = this.info.as_shot_neutral
		const asShot = undefined // TODO: read from rawspeed side

		let white = new XYCoord()

		if (!asShot) {
			if (!neutral) {
				console.log("[dng-white-balance] Missing DNG white balance information.")
				return
			}

			// TODO: support non-3 number of channels
			const vec: number[] = new Array(neutral.length).fill(0)

			for (let c = 0; c < neutral.length; c++) {
				vec[c] = neutral[c] ?? 1.0
			}

			const unify = 1.0 / Math.max(...vec)

			for (let c = 0; c < neutral.length; c++) {
				vec[c] = (vec[c] ?? 1.0) * unify
			}

			white = this.computeNeutralToXY(vec as vec3)
		} else {
			white = new XYCoord([asShot[0], asShot[1]])
		}

		this.computedWhite = white

		/* Computing camera white multipliers */

		if (this.channels == 1) {
			//TODO: finalise
			this.computedCameraWhite = [1, 1, 1]
			this.computedCamToPCS = mat3.create()
			return
		}

		const colorMatrix = this.computeXYZToCamera(this.computedWhite)
		const forwardMatrix: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]
		const cameraCalibration: mat3 = [0, 0, 0, 0, 0, 0, 0, 0, 0]

		this.computedCameraWhite = multiplyMat3ByVec3(colorMatrix, white.XYtoXYZ())

		const whiteScale = 1.0 / Math.max(...this.computedCameraWhite)

		for (let j = 0; j < this.channels; j++) {
			this.computedCameraWhite[j] = clamp(
				whiteScale * (this.computedCameraWhite[j] ?? 0.0),
				0.001,
				1.0,
			)
		}

		/* Computing PCS matrices */

		this.computedPCSToCam = multiplyMat3(
			colorMatrix,
			this.mapWhiteMatrix(XYCoord.PCStoXY(), white),
		)

		const scale = Math.max(...multiplyMat3ByVec3(this.computedPCSToCam, XYCoord.PCStoXYZ()))

		for (let i = 0; i < this.computedPCSToCam.length; i++) {
			this.computedPCSToCam[i] = (1.0 / scale) * (this.computedPCSToCam[i] ?? 0.0)
		}

		if (forwardMatrix && isEmptyMat3(forwardMatrix) == false) {
			// TODO: validate
			const individualToReference = invertMat3(
				multiplyMat3(this.analogBalance, cameraCalibration),
			)
			const refCameraWhite = multiplyMat3ByVec3(
				individualToReference,
				this.computedCameraWhite,
			)

			this.computedCamToPCS = multiplyMat3(
				forwardMatrix,
				multiplyMat3(invertMat3(vec3ToMat3Diagonal(refCameraWhite)), individualToReference),
			)
		} else {
			// TODO: handle multiplication with reduction matrix when > 3 channels
			this.computedCamToPCS = invertMat3(this.computedPCSToCam)
		}
	}

	private mapWhiteMatrix(white1: XYCoord, white2: XYCoord) {
		const Mb: mat3 = [0.8951, 0.2664, -0.1614, -0.7502, 1.7135, 0.0367, 0.0389, -0.0685, 1.0296]

		const w1 = multiplyMat3ByVec3(Mb, white1.XYtoXYZ())
		const w2 = multiplyMat3ByVec3(Mb, white2.XYtoXYZ())

		w1[0] = Math.max(w1[0], 0.0)
		w1[1] = Math.max(w1[1], 0.0)
		w1[2] = Math.max(w1[2], 0.0)

		w2[0] = Math.max(w2[0], 0.0)
		w2[1] = Math.max(w2[1], 0.0)
		w2[2] = Math.max(w2[2], 0.0)

		const A: mat3 = mat3.create()

		A[0] = clamp(w1[0] > 0.0 ? w2[0] / w1[0] : 10.0, 0.1, 10.0)
		A[4] = clamp(w1[1] > 0.0 ? w2[1] / w1[1] : 10.0, 0.1, 10.0)
		A[8] = clamp(w1[2] > 0.0 ? w2[2] / w1[2] : 10.0, 0.1, 10.0)

		const B = multiplyMat3(multiplyMat3(invertMat3(Mb), A), Mb)
		return B
	}

	private computeNeutralToXY(neutral: vec3) {
		if (this.channels == 1) {
			return XYCoord.PCStoXY()
		}

		const maxPasses = 30
		let last = XYCoord.D50()

		for (let pass = 0; pass < maxPasses; pass++) {
			const xyzToCamera = this.computeXYZToCamera(last)
			const inv = invertMat3(xyzToCamera)
			const vec = multiplyMat3ByVec3(inv, neutral)

			const next = XYCoord.XYZtoXY(vec)

			if (Math.abs(next.x - last.x) + Math.abs(next.y - last.y) < 0.0000001) {
				return next
			}

			if (pass == maxPasses - 1) {
				next.x = (last.x + next.x) * 0.5
				next.y = (last.y + next.y) * 0.5
			}
			last = next
		}

		return last
	}

	private computeXYZToCamera(
		white: XYCoord,
		// forwardMatrix: mat3,
		// reductionMatrix: mat3,
		// cameraCalibration: mat3,
	): mat3 {
		/* WB */

		const td = new DNGTemperature(white)

		let g

		if (td.temperature <= this.temperature1) {
			g = 1.0
		} else if (td.temperature >= this.temperature2) {
			g = 0.0
		} else {
			const invT = 1.0 / td.temperature

			g =
				(invT - 1.0 / this.temperature2) /
				(1.0 / this.temperature1 - 1.0 / this.temperature2)
		}

		/* Matrices */

		let colorMatrix: mat3

		if (g >= 1.0) {
			colorMatrix = this.colorMatrix1
		} else if (g <= 0.0) {
			colorMatrix = this.colorMatrix2
		} else {
			colorMatrix = interpolateMat3(this.colorMatrix1, this.colorMatrix2, g)
		}

		// TODO: compute forward matrix
		// TODO: compute reduction
		// TODO: compute camera calibration

		return colorMatrix
	}

	private normalizeForwardMatrix(mat: mat3) {
		// TODO: implement
		return mat
	}

	private convertIlluminantToTemperature(illuminant: DNGCalibrationIlluminant): number {
		switch (illuminant) {
			case DNGCalibrationIlluminant.Unknown:
				return 0.0
			case DNGCalibrationIlluminant.Daylight:
				return 5500.0
			case DNGCalibrationIlluminant.Fluorescent:
				return (3800.0 + 4500.0) * 0.5
			case DNGCalibrationIlluminant.Tungsten:
				return 2850.0
			case DNGCalibrationIlluminant.Flash:
				return 5500.0
			case DNGCalibrationIlluminant.FineWeather:
				return 5500.0
			case DNGCalibrationIlluminant.CloudyWeather:
				return 6500.0
			case DNGCalibrationIlluminant.Shade:
				return 7500.0
			case DNGCalibrationIlluminant.DaylightFluorescent:
				return (5700.0 + 7100.0) * 0.5
			case DNGCalibrationIlluminant.DayWhiteFluorescent:
				return (4600.0 + 5500.0) * 0.5
			case DNGCalibrationIlluminant.CoolWhiteFluorescent:
				return (3800.0 + 4500.0) * 0.5
			case DNGCalibrationIlluminant.WhiteFluorescent:
				return (3250.0 + 3800.0) * 0.5
			case DNGCalibrationIlluminant.WarmWhiteFluorescent:
				return (2600.0 + 3250.0) * 0.5
			case DNGCalibrationIlluminant.StandardLightA:
				return 2850.0
			case DNGCalibrationIlluminant.StandardLightB:
				return 5500.0
			case DNGCalibrationIlluminant.StandardLightC:
				return 6500.0
			case DNGCalibrationIlluminant.D55:
				return 5500.0
			case DNGCalibrationIlluminant.D65:
				return 6500.0
			case DNGCalibrationIlluminant.D75:
				return 7500.0
			case DNGCalibrationIlluminant.D50:
				return 5000.0
			case DNGCalibrationIlluminant.ISOStudioTungsten:
				return 3200.0
			case DNGCalibrationIlluminant.OtherLightSource:
				return 0.0
			default:
				return 0.0
		}
	}
}
